import { ReactElement, ReactNode, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, SxProps, Tooltip, ClickAwayListener } from '@mui/material';
import { getDeviceInfo } from 'state/localStorageTyped';

type InfoTooltipProps = {
  title: ReactNode;
  iconSx?: SxProps;
  children?: ReactElement;
};

/**
 * A consolidated info icon with a tooltip containing helper text. Unlike the
 * vanilla MUI tooltip, this will be triggered on mobile by a simple touch,
 * not requiring a long-press.
 *
 * Optionally pass `children` to use a custom element as the tooltip trigger
 * instead of the default info icon.
 */
export const InfoTooltip = (props: InfoTooltipProps): JSX.Element => {
  const { title, iconSx = {}, children } = props;
  const [open, setOpen] = useState(false);
  const deviceInfo = getDeviceInfo();
  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  const defaultIcon = (
    <InfoOutlinedIcon
      fontSize="small"
      sx={{ width: 16, height: 16, ...iconSx }}
    />
  );

  const triggerElement = children ?? defaultIcon;

  if (!isMobilePlatform) {
    return (
      <Tooltip arrow title={title} placement="top">
        {triggerElement}
      </Tooltip>
    );
  }

  return (
    <ClickAwayListener
      onClickAway={() => setOpen(false)}
      touchEvent="onTouchStart"
      mouseEvent={false}
    >
      <Tooltip
        arrow
        open={open}
        title={title}
        placement="top"
        onOpen={() => setOpen(true)}
      >
        <Box
          component="span"
          onClick={() => setOpen(true)}
          // inline-flex so the icon aligns correctly inside a flex row container.
          sx={{ display: 'inline-flex' }}
        >
          {triggerElement}
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};
